"use client";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { getBillingProfile } from "~/lib/network/billing";
import GamgeeCoin from "~/components/price-comparison/gamgee-coin";
import PriceCard from "./price-card";

const plans = [
  {
    name: "Basic",
    api_payload: "basic",
    price: 20,
    badge: null,
    features: [
      "100 coins monthly",
      "10 hours of notebook usage (S-profile)",
      "Great for solo/POC",
    ],
  },
  {
    name: "Pro",
    api_payload: "pro",
    price: 50,
    badge: "Most Popular",
    gradientClass: "border-2 border-primary/40",
    features: ["300 coins monthly", "30 hours of notebook usage (S-profile)"],
  },
  {
    name: "Max",
    api_payload: "max",
    price: 100,
    badge: "Best Value",
    features: ["700 coins monthly", "60 hours of notebook usage (S-profile)"],
  },
  {
    name: "Enterprise",
    price: "Custom",
    features: [
      "Custom usage limits",
      "Reserved GPUs and Compute",
      "Single sign-on (SSO) and domain capture",
      "Role-based access with fine grained permissioning",
      "System for Cross-domain Identity Management (SCIM)",
      "Compliance API for observability and monitoring",
    ],
  },
];

// Animated price component with slide transitions

export default function PriceComparison({
  billingModalOpen,
  setBillingModalOpen,
}: {
  billingModalOpen: boolean;
  setBillingModalOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnnual, setIsAnnual] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const discount = 0.15; // 15% discount

  // Fetch user's current billing plan
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const profile = await getBillingProfile();
        setCurrentPlan(profile.billing_plan.toLowerCase());
      } catch (error) {
        console.error("Failed to fetch billing profile:", error);
      } finally {
        setLoading(false);
      }
    };

    if (billingModalOpen) {
      fetchCurrentPlan();
    }
  }, [billingModalOpen]);

  const handleUpgrade = (plan: "basic" | "pro" | "max") => {
    // Close the modal and navigate to billing page with selected plan
    setBillingModalOpen(false);
    navigate(
      `/billing?plan=${plan}&returnRoute=${encodeURIComponent(
        location.pathname
      )}`
    );
  };

  return (
    <dialog id="billing-modal" className="modal" open={billingModalOpen}>
      {billingModalOpen && (
        <div className="bg-base-300 w-full h-full overflow-y-auto overflow-hidden">
          <button
            type="button"
            className="btn btn-sm btn-circle btn-soft btn-primary absolute top-6 right-6"
            onClick={() => setBillingModalOpen(false)}
          >
            ✕
          </button>
          <div className="w-full max-w-7xl my-6 m-auto">
            <div className="w-full h-full mx-auto mt-22 bg-base-300">
              <div className="flex justify-center items-center mb-12">
                <div className="indicator">
                  <span className="indicator-item badge badge-sm badge-success badge-soft">
                    Save 15%
                  </span>
                  <div className="tabs tabs-box">
                    <input
                      type="radio"
                      name="pricing_tabs"
                      className="tab"
                      aria-label="Monthly"
                      checked={!isAnnual}
                      onChange={() => setIsAnnual(false)}
                    />
                    <input
                      type="radio"
                      name="pricing_tabs"
                      className="tab"
                      aria-label="Annual"
                      checked={isAnnual}
                      onChange={() => setIsAnnual(true)}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {plans.map((plan) => (
                  <PriceCard
                    key={plan.name}
                    plan={plan}
                    isAnnual={isAnnual}
                    discount={discount}
                    currentPlan={currentPlan}
                    loading={loading}
                    onUpgrade={handleUpgrade}
                  />
                ))}
              </div>

              {/* Coin Usage Information */}
              <div className="mt-12 p-6 bg-base-100 rounded-lg max-w-xl mx-auto text-center">
                <p className="text-sm text-base-content/90 mb-3">
                  Coins track your{" "}
                  <span className="text-success font-semibold">AI usage</span>{" "}
                  based on tokens consumed
                </p>
                <p className="text-xs text-base-content/60 mb-4">
                  ~2 coins per message
                </p>
                <div className="badge badge-success badge-soft badge-lg p-4 text-sm flex gap-2 m-auto">
                  <span>
                    <GamgeeCoin size={20} />
                  </span>{" "}
                  Free: 20 coins monthly
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </dialog>
  );
}
